import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import {
  CohortQuestionDistributionsResponse,
  ProgramSurveyAnalyticsDTO,
  ProgramSurveysListResponse,
  SurveyCourseDetailDTO,
} from '@app/app/shared/services/survey-analytics/survey-analytics.model';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  AdminCoursesRequest,
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  ProgramSkillsOverviewResponse,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

/** Course analytics endpoints live on the analytics microservice; respect the local override if set. */
const COURSE_ANALYTICS_API_ROOT = Location.joinWithSlash(
  environment.localServiceOverrides?.analyticsBaseUrl || environment.apiRootUrl || '',
  'analytics/courses',
);

/**
 * Course-scoped implementation of the analytics interface.
 *
 * Reuses the same `CoursesAnalyticsDataService` contract (and therefore the same
 * shared cohort-comparison / survey-analytics components) but targets the
 * single-course endpoints `analytics/courses/{courseId}/...`. The `id` argument
 * on every method is a courseId rather than a programId.
 *
 * Client-org variants (`*ByOrgId`) fall back to the plain course endpoints –
 * there are currently no `analytics/clients/{org}/courses/{courseId}/...` routes.
 */
@Injectable()
export class ApiCourseAnalyticsDataService implements CoursesAnalyticsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getAdminCourses(requestParams?: AdminCoursesRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (requestParams?.programmeId) {
      params['inProgram'] = requestParams.programmeId;
    }
    if (requestParams?.orgClientId) {
      params['orgClientId'] = requestParams.orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }

    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(COURSE_ANALYTICS_API_ROOT, 'admin'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
    );
  }

  getOverview(courseId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    const params = this.buildBaseParams(filters);
    return this.client
      .get<ProgramCoursesOverview>([COURSE_ANALYTICS_API_ROOT, courseId, 'overview'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsOverview'))),
      );
  }

  getOverviewByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCoursesOverview> {
    return this.getOverview(courseId, filters);
  }

  getProgramSkillsOverview(courseId: string): ObservableResult<ProgramSkillsOverviewResponse> {
    return this.client
      .get<ProgramSkillsOverviewResponse>([COURSE_ANALYTICS_API_ROOT, courseId, 'skills'].reduce(Location.joinWithSlash))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsSkills'))),
      );
  }

  /**
   * No single-course "list of courses" endpoint exists. The course-overview
   * table and the grades improvement/final views derive from this; until the
   * overview endpoint carries per-row data they render their empty state.
   */
  getCourses(_courseId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    return ObservableResult.ofSuccess([]);
  }

  getCoursesByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCourseItem[]> {
    return this.getCourses(courseId, filters);
  }

  getStudentEngagement(courseId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    const params = this.buildBaseParams(filters);
    return this.client
      .get<StudentEngagementData>([COURSE_ANALYTICS_API_ROOT, courseId, 'engagement'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsEngagement'))),
      );
  }

  getStudentEngagementByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<StudentEngagementData> {
    return this.getStudentEngagement(courseId, filters);
  }

  getCohorts(courseId: string): ObservableResult<CohortOption[]> {
    return this.client.get<CohortOption[]>([COURSE_ANALYTICS_API_ROOT, courseId, 'cohorts'].reduce(Location.joinWithSlash)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohorts'))),
    );
  }

  getCohortsByOrgId(_clientOrgId: string, courseId: string): ObservableResult<CohortOption[]> {
    return this.getCohorts(courseId);
  }

  getCohortGradeComparison(courseId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metric) {
      params['metric'] = filters.metric.toUpperCase();
    }
    if (filters?.dateRange?.start) {
      params['startDate'] = filters.dateRange.start.toISOString();
    }
    if (filters?.dateRange?.end) {
      params['endDate'] = filters.dateRange.end.toISOString();
    }

    return this.client
      .get<CohortGradeData[]>([COURSE_ANALYTICS_API_ROOT, courseId, 'cohort-comparison'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohortComparison')),
        ),
      );
  }

  getCohortGradeComparisonByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<CohortGradeData[]> {
    return this.getCohortGradeComparison(courseId, filters);
  }

  getSurveyAnalyticsByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metricType) {
      params['metricType'] = filters.metricType;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramSurveyAnalyticsDTO>([COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveys'))),
      );
  }

  getProgramSurveysListByOrgId(_clientOrgId: string, courseId: string, client?: string): ObservableResult<ProgramSurveysListResponse> {
    const params: Record<string, string> = {};
    if (client) {
      params['client'] = client;
    }

    return this.client
      .get<ProgramSurveysListResponse>([COURSE_ANALYTICS_API_ROOT, courseId, 'surveys'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadProgramSurveys')),
        ),
      );
  }

  getQuestionDistributionsByOrgId(
    _clientOrgId: string,
    courseId: string,
    filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.courseNames?.length) {
      params['courseNames'] = filters.courseNames.join(',');
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<CohortQuestionDistributionsResponse>(
        [COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics', 'question-distributions'].reduce(Location.joinWithSlash),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveyQuestionAnalytics')),
        ),
      );
  }

  getCourseDetailByOrgId(
    _clientOrgId: string,
    courseId: string,
    courseName: string,
    cohortNames?: string[],
    company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    const params: Record<string, string> = { courseName };
    if (cohortNames?.length) {
      params['cohortNames'] = cohortNames.join(',');
    }
    if (company) {
      params['company'] = company;
    }

    return this.client
      .get<SurveyCourseDetailDTO>(
        [COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics', 'course-detail'].reduce(Location.joinWithSlash),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadCourseDetail'))),
      );
  }

  getSurveyAnalytics(
    courseId: string,
    filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metricType) {
      params['metricType'] = filters.metricType;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramSurveyAnalyticsDTO>([COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics'].reduce(Location.joinWithSlash), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadProgramSurveys')),
        ),
      );
  }

  getSurveyCourseDetail(
    courseId: string,
    courseName: string,
    cohortNames?: string[],
    company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    const params: Record<string, string> = { courseName };
    if (cohortNames?.length) {
      params['cohortNames'] = cohortNames.join(',');
    }
    if (company) {
      params['company'] = company;
    }

    return this.client
      .get<SurveyCourseDetailDTO>(
        [COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics', 'course-detail'].reduce(Location.joinWithSlash),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadCourseDetail'))),
      );
  }

  getCohortQuestionDistributions(
    courseId: string,
    filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.courseNames?.length) {
      params['courseNames'] = filters.courseNames.join(',');
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<CohortQuestionDistributionsResponse>(
        [COURSE_ANALYTICS_API_ROOT, courseId, 'survey-analytics', 'question-distributions'].reduce(Location.joinWithSlash),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.surveyAnalytics.errors.loadSurveyQuestionAnalytics')),
        ),
      );
  }

  private buildBaseParams(filters?: ProgramCoursesAnalyticsFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }
    return params;
  }
}
